import React, { useState } from "react";
import Button from "../../components/Button";
import SmartForm from "../../components/SmartForm";
import { getCounterBlockedStatus, getNongstMtdLimit, putAcceptOrderRequest, putRejectOrderRequest } from "src/services/order-api";
import { RootState } from "src/context/store";
import { useSelector } from "react-redux";
import { useToast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import FormSuccess from "../../container/FormSuccess";

interface OrderApprovalFormProps {
  type: boolean;
  order_id: number;
  closeModal: (modalName: string) => void;
  onSuccess: (streamId: string) => void;
  position_code: string;
  counter_company_location_id?: number;
}

const OrderApprovalForm: React.FC<OrderApprovalFormProps> = ({ type, order_id, closeModal, onSuccess, position_code, counter_company_location_id }) => {
  const { orderDetailsFiltered } = useSelector((state: RootState) => state.ord);
  const [remarks, setRemarks] = useState("");
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const orderDetails = orderDetailsFiltered?.[0];
  const [isNonGSTOrderLimitModalOpen, setIsNonGSTOrderLimitModalOpen] = useState(false);
  const [gstLimitExceededMessage, setGstLimitExceededMessage] = useState<string>("");

  const [approvalData, setApprovalData] = useState<any>(null);

  const { withEventCheck } = useEventStatus();

  const handleSubmitRequest = async (data: any, isNonGSTOrderLimitPass: boolean) => {
    setIsLoading(true);
    try {
      const counterBlockedStatus = await getCounterBlockedStatus(orderDetails?.counter_ff_div_lnk_id);
      // const isCounterBlocked =
      //   counterBlockedStatus?.data?.data?.counter_order_block?.length > 0 ||
      //   counterBlockedStatus?.data?.data?.counter_company_lnk_id?.counter_order_block?.length > 0;
      // const nongst_orderlimit_value = counterBlockedStatus?.data?.data?.counter_company_lnk_id?.company_code?.nongst_orderlimit_value ?? null;
      const gstAvailable = counterBlockedStatus?.data?.data?.counter_company_lnk_id?.counter_location?.counter_hdr_id?.gst_available === "Yes";
      // setNonGstOrderLimitValue(nongst_orderlimit_value);

      if (!gstAvailable && !isNonGSTOrderLimitPass && type === true) {
        try {
          const nongstMtdLimit = await getNongstMtdLimit({
            counter_company_location_id: orderDetails?.counter_company_location_id,
            company_code: orderDetails?.company_code,
            division_codes: orderDetails?.company_divisioncode,
            order_values: orderDetails?.billed_price,
          });
          const updatedDivisionsData = nongstMtdLimit?.data?.data?.data?.divisions;
          const hasExceeded = Object.values(updatedDivisionsData || {}).some((division: any) => division.is_limit_exceeded);
          if (hasExceeded) {
            const message = Object.values(updatedDivisionsData || {})
              .filter((division: any) => division.is_limit_exceeded)
              .map(
                (division: any) =>
                  `For division "${orderDetails?.division_name}" the limit is ${division.limit_value}, remaining limit is ${division.remaining_limit}, and current order used value is ${division?.order_value}.`
              )
              .join("\n");
            setGstLimitExceededMessage(message);
            setIsNonGSTOrderLimitModalOpen(hasExceeded);
            setIsLoading(false);
          } else {
            type ? acceptRequestHandler(data) : rejectRequestHandler(data);
          }
          return;
        } catch (error: any) {
          toast({
            description: error?.response?.data?.message || "Failed to validate non-GST order limit. Please try again.",
            variant: "destructive",
          });
          console.log(error);
        }
      } else {
        type ? acceptRequestHandler(data) : rejectRequestHandler(data);
      }
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Failed to approve order", variant: "destructive" });
      setIsLoading(false);
    }
  };

  async function acceptRequestHandler(data: any) {
    setIsLoading(true);
    if (!data?.remarks?.trim()) {
      toast({
        description: "Comment cannot be empty",
        variant: "destructive",
      });
      return;
    }
    try {
      if (isLoading) return;
      setIsLoading(true);
      const payload = {
        data: {
          position_code: position_code,
          order_id: order_id,
          action: "approve",
          remarks: data?.remarks?.trim(),
          counter_company_location_id: counter_company_location_id,
        },
        event_type: "ORDER_APPROVE",
        primary_key: order_id,
      };
      console.log(order_id);
      const res = await putAcceptOrderRequest(payload, orderDetails?.company_code);

      if (res.status !== 200) throw new Error();
      setTimeout(() => {
        onSuccess(res?.data?.data?.data?.eventStreamCreated?.streamId);
        closeModal("approveCounter");

        setIsLoading(false);
      }, 1500);

      toast({
        description: res?.data?.message || "Order Approved successfully",
        variant: "default",
      });
    } catch (error: any) {
      console.log(error);
      setIsLoading(false);
      closeModal("approveCounter");
      toast({
        description: error?.response?.data?.message,
        variant: "destructive",
      });
    }
  }

  async function rejectRequestHandler(data: any) {
    setIsLoading(true);
    await withEventCheck(() => {}, `order_${orderDetails?.order_id_int}`,orderDetails?.company_code); //M10-12758
    if (!data?.remarks?.trim()) {
      toast({
        description: "Comment cannot be empty",
        variant: "destructive",
      });
      return;
    }
    try {
      if (isLoading) return;
      setIsLoading(true);
      const payload = {
        data: {
          position_code: position_code,
          order_id: order_id,
          remarks: data?.remarks?.trim(),
        },
        event_type: "ORDER_REJECT",
        primary_key: order_id,
      };

      const res = await putRejectOrderRequest(payload, orderDetails?.company_code);

      if (res.status !== 200) throw new Error();
      setTimeout(() => {
        onSuccess(res?.data?.data?.data?.eventStreamCreated?.streamId);
        closeModal("rejectCounter");

        setIsLoading(false);
      }, 1500);
      toast({
        description: res?.data?.message || "Order rejected successfully",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || "Failed to reject order",
        variant: "destructive",
      });
    }
  }

  return (
    <SmartForm
      isControlled={false}
      defaultValues={{}}
      onFormSubmit={(data: any) => {
        setApprovalData(data);
        handleSubmitRequest(data, false);
      }}
    >
      <section className="flex flex-col gap-2 px-5 py-5 w-100">
        <SmartForm.TextArea
          type="text"
          value={remarks}
          onChange={(e: any) => {
            console.log(e.target.value);

            setRemarks(e.target.value);
          }}
          className=""
          placeholder="Type comment here..."
          fieldName="remarks"
          label="Comments"
        />
      </section>
      <footer className="border border-t-[1px] flex justify-end px-5 py-3 gap-3 rounded-bl-2xl rounded-br-2xl">
        <Button className="border" onClick={() => (type ? closeModal("approveCounter") : closeModal("rejectCounter"))}>
          Cancel
        </Button>
        <Button loaderSize="small" isLoading={isLoading} type="submit" className="text-white bg-[#586AF5]">
          Submit
        </Button>
      </footer>
      <FormSuccess
        onCloseModal={() => {
          setIsNonGSTOrderLimitModalOpen(false);
        }}
        showForm={isNonGSTOrderLimitModalOpen}
        onContinue={() => {
          setIsNonGSTOrderLimitModalOpen(false);
          // checkUINValidation()
          handleSubmitRequest(approvalData, true);
        }}
        enableBackButton={true}
        variant="error"
        popupMessage={{
          description: gstLimitExceededMessage,
          title: "Non-GST Order Limit!",
        }}
      />
    </SmartForm>
  );
};

export default OrderApprovalForm;
